import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Container, Row, Col, Form, Button, Spinner } from 'react-bootstrap';
import { post, get, patch } from '../../../../helpers/httpClient';
import { toast } from 'react-toastify';
import { renderEditorDataToHTML } from '../../../../helpers/Editor';
import RichTextEditor, { safeParseBody } from '../../../../helpers/RichTextEditor';
import PageBasicFields from "../../../../helpers/PageBasicFields";

function GalleryEditor() {
    const navigate = useNavigate();
    const { id, pageId } = useParams();
    const [searchParams] = useSearchParams();
    const isEdit = !!pageId;

    const [form, setForm] = useState({ slug: '', title: '', template: null, extra: { galleryTemplateId: '', elementsInARow: 3 } });
    const [saving, setSaving] = useState(false);
    const [loading, setLoading] = useState(true);
    const [galleryTemplates, setGalleryTemplates] = useState([]);

    const handleEditorChange = (data) => {
        setForm((prev) => ({
            ...prev,
            extra: {
                ...(prev.extra || {}),
                content: { ...(prev.extra?.content || {}), body: data },
            },
        }));
    };

    const titleText = useMemo(() => (isEdit ? 'Edit Gallery' : 'New Gallery'), [isEdit]);

    useEffect(() => {
        let isMounted = true;
        async function load() {
            try {
                setLoading(true);
                const [templatesResp, pageResp] = await Promise.all([
                    get(`websites/${id}/gallery-templates`),
                    isEdit ? get(`pages/${pageId}`) : Promise.resolve(null),
                ]);
                if (!isMounted) return;
                setGalleryTemplates(templatesResp?.gallery_templates ?? []);
                if (isEdit) {
                    const page = pageResp?.page || pageResp;
                    if (!page) throw new Error('Not found');
                    setForm({
                        slug: page.slug || '',
                        title: page.title || '',
                        description: page.description || '',
                        preview: page.preview,
                        template: page.template || null,
                        components: page.components || { items: [] },
                        extra: page.extra || { galleryTemplateId: '', elementsInARow: 3 },
                    });
                }
            } catch {
                if (isMounted) toast.error('Failed to load data.');
            } finally {
                if (isMounted) setLoading(false);
            }
        }
        load();
        return () => { isMounted = false; };
    }, [id, pageId, isEdit]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.title || !form.title.trim()) {
            toast.error('Title is required.');
            return;
        }

        const payload = {
            type: 'gallery',
            slug: (form.slug || '').trim(),
            title: (form.title || '').trim(),
            description: (form.description || '').trim() || null,
            preview: form.preview || null,
            template: form.template || null,
            components: form.components || [],
            extra: (() => {
                const bodyObj = safeParseBody(form?.extra?.content?.body);
                return {
                    ...(form.extra || {}),
                    content: {
                        body: JSON.stringify(bodyObj),
                        rendered: renderEditorDataToHTML(bodyObj),
                    },
                };
            })(),
        };

        try {
            setSaving(true);
            if (isEdit) {
                await patch(`websites/${id}/pages/${pageId}`, payload);
            } else {
                const parent = searchParams.get('parent');
                await post(`websites/${id}/pages`, { ...payload, order: 0, parent: parent || null });
            }
            navigate(`/websites/${id}?tab=structure`);
        } catch {
            toast.error(isEdit ? 'Failed to update page.' : 'Failed to create page.');
        } finally {
            setSaving(false);
        }
    };

    const handleCancel = () => navigate(-1);

    return (
        <Container className="mt-4">
            <Row>
                <Col>
                            <div className="structure-node-header mb-3">
                                <h2 className="m-0">{titleText}</h2>
                                <span className="page-type-badge page-type-badge--gallery">Gallery</span>
                            </div>

                            {loading ? (
                                <div className="text-center my-3"><Spinner animation="border" /></div>
                            ) : (
                                <Form onSubmit={handleSubmit}>
                                    <PageBasicFields
                                      form={form}
                                      setForm={setForm}
                                      websiteId={id}
                                      slugPlaceholder="e.g. products"
                                      titlePlaceholder="Enter title"
                                      preserveEmptySlug={isEdit}
                                    />

                                    <div className="props-panel">
                                        <h6 className="theme-group-label mb-3">Content</h6>
                                        <RichTextEditor value={form.extra?.content?.body} onChange={handleEditorChange} websiteId={id} />
                                    </div>

                                    <div className="props-panel">
                                        <h6 className="theme-group-label mb-3">Layout</h6>
                                        <div className="form-row-horizontal mb-2">
                                            <Form.Label>Elements in a row</Form.Label>
                                            <div className="row-selector">
                                              {[1, 2, 3, 4, 5].map((n) => {
                                                const active = (form.extra?.elementsInARow ?? 3) === n;
                                                return (
                                                  <button
                                                    key={n}
                                                    type="button"
                                                    className={`row-selector-btn${active ? ' row-selector-btn--active' : ''}`}
                                                    onClick={() => setForm(f => ({ ...f, extra: { ...f.extra, elementsInARow: n } }))}
                                                    title={`${n} column${n > 1 ? 's' : ''}`}
                                                  >
                                                    <div className="row-selector-grid" style={{ gridTemplateColumns: `repeat(${n}, 1fr)` }}>
                                                      {Array.from({ length: n }).map((_, i) => <div key={i} className="row-selector-cell" />)}
                                                    </div>
                                                    <span className="row-selector-num">{n}</span>
                                                  </button>
                                                );
                                              })}
                                            </div>
                                        </div>

                                        <div className="form-row-horizontal mb-2">
                                            <Form.Label>Gallery template</Form.Label>
                                            <div>
                                                <Form.Select
                                                  value={form.extra?.galleryTemplateId ?? ''}
                                                  onChange={(e) => setForm(f => ({ ...f, extra: { ...f.extra, galleryTemplateId: e.target.value } }))}
                                                >
                                                    <option value="">— None —</option>
                                                    {galleryTemplates.map((t) => (
                                                        <option key={t.id} value={t.id}>{t.name}</option>
                                                    ))}
                                                </Form.Select>
                                                <Link to={`/websites/${id}?tab=gallery-templates`} className="small mt-1 d-block">Manage templates</Link>
                                            </div>
                                        </div>

                                    </div>

                                    <div className="form-actions">
                                        <Button type="submit" className="primary-button" disabled={saving}>
                                            {saving ? (<><Spinner animation="border" size="sm" className="me-2" /> Saving...</>) : 'Save'}
                                        </Button>
                                        <Button className="primary-button" onClick={handleCancel} disabled={saving}>Cancel</Button>
                                    </div>
                                </Form>
                            )}
                </Col>
            </Row>
        </Container>
    );
}

export default GalleryEditor;
